import { Button, message } from 'antd';
import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColorBlockSpecsFragment } from '../../../../api/fragments/ColorBlockSpecs';
import { type MatrixSpecsFragment } from '../../../../api/fragments/MatrixSpecs';
import { type TrimBlockSpecsFragment } from '../../../../api/fragments/TrimBlockSpecs';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import useDebounce from '../../../../utilities/useDebounce';
import useHandleError from '../../../../utilities/useHandleError';
import { type VariantConfiguratorDetailsPageProps } from '../utils';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import VariantConfiguratorLayout from '../VariantConfiguratorLayout';
import TrimBlockForm from './TrimBlockForm';
import useHandleBlockSave from './useHandleBlockSave';
import useTrimBlockValidator from './useTrimBlockValidator';
import { type TrimBlockFormValues, getDefaultTrimBlock, FORM_NAME } from './utils';

const TrimBlockTab = ({ activeTab, replaceTab, disabled = false }: VariantConfiguratorDetailsPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);
    const { variantConfigurator, refetch } = useVariantConfigurator();

    const [colorBlock, trimBlock, matrix] = useMemo(
        () => [variantConfigurator.colorBlock, variantConfigurator.trimBlock, variantConfigurator.matrix],
        [variantConfigurator]
    );

    const handleBlockSave = useHandleBlockSave();

    const getInitialValues = useCallback(
        (
            block: TrimBlockSpecsFragment,
            colorBlock?: ColorBlockSpecsFragment,
            matrix?: MatrixSpecsFragment[]
        ): TrimBlockFormValues => {
            if (!block) {
                return getDefaultTrimBlock(colorBlock);
            }

            const trimColorCombo = matrix?.reduce((accumulator, currentValue) => {
                const previousColorAccumulator = { ...accumulator[currentValue.trimId] };

                return {
                    ...accumulator,
                    [currentValue.trimId]: {
                        ...previousColorAccumulator,
                        [currentValue.colorId]: currentValue.variantTrimImage,
                    },
                };
            }, {});

            const trimSettings = block.trimSettings.map(trim => {
                const sectionImages = colorBlock?.colorSettings.map(color => ({
                    colorId: color.id,
                    colorName: color.name,
                    variantTrimImage: color?.id ? trimColorCombo[trim.id]?.[color.id] : null,
                }));

                return {
                    hex: trim.hex,
                    name: trim.name,
                    code: trim.code,
                    id: trim.id,
                    price: trim.price,
                    image: trim.image,
                    sectionImages,
                };
            });

            return {
                type: block.type,
                trimBlockInput: {
                    id: block.id,
                    isActive: block.isActive,
                    sectionTitle: block.sectionTitle,
                    trimSettings,
                },
            };
        },
        []
    );

    const onSubmit = useDebounce(
        useHandleError<TrimBlockFormValues>(
            async (values, helpers) => {
                message.loading({
                    content: t('variantConfiguratorDetails:messages.trim.saveSubmitting'),
                    key: 'primary',
                    duration: 0,
                });

                await handleBlockSave(values, variantConfigurator.id, trimBlock);

                message.success({
                    content: t('variantConfiguratorDetails:messages.trim.saveSuccessful'),
                    key: 'primary',
                });

                const updatedVariantConfigurator = await refetch();
                helpers.resetForm({
                    values: getInitialValues(
                        updatedVariantConfigurator?.trimBlock,
                        updatedVariantConfigurator?.colorBlock,
                        updatedVariantConfigurator?.matrix
                    ),
                });
            },
            [getInitialValues, handleBlockSave, refetch, t, trimBlock, variantConfigurator.id]
        )
    );
    const validate = useTrimBlockValidator();

    return (
        <VariantConfiguratorLayout
            activeTab={activeTab}
            footer={[
                !disabled && (
                    <Button key="submit" form={FORM_NAME} htmlType="submit" type="primary">
                        {t('variantConfiguratorDetails:actions.trims.save')}
                    </Button>
                ),
            ]}
            replaceTab={replaceTab}
        >
            <Formik<TrimBlockFormValues>
                initialValues={getInitialValues(trimBlock, colorBlock, matrix)}
                onSubmit={onSubmit}
                validate={validate}
            >
                {({ handleSubmit, dirty }) => (
                    <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <TrimBlockForm colorBlock={colorBlock} disabled={disabled} />
                    </Form>
                )}
            </Formik>
        </VariantConfiguratorLayout>
    );
};

export default TrimBlockTab;
